/**
 * Share Service for creating and retrieving shareable SyntaxLens content
 */

import { getAPIHeaders } from '../utils/apiHeaders';

export interface GenerationMetadata {
  model?: string;
  provider?: string;
  promptVersion?: string;
  isStreaming?: boolean;
  quality?: 'fast' | 'quality';
  [key: string]: any; // Allow additional metadata fields
}

export interface ShareData {
  code: string;
  sourceLanguage: string;
  targetLanguage: string;
  htmlContent: string;
  generationMetadata?: GenerationMetadata;
}

export interface ShareResponse {
  id: string;
  shareUrl: string;
  gcsUrl: string;
  createdAt: string;
  accessCount: number;
  contentFingerprint?: string; // Renamed from codeHash (for similarity detection, not deduplication)
  generationMetadata?: GenerationMetadata; // Stored but not displayed to users yet
}

export interface ShareDetails extends ShareResponse {
  code: string;
  htmlContent: string;
  sourceLanguage: string;
  targetLanguage: string;
}

class ShareService {
  private baseUrl: string;

  constructor() {
    // Use environment variable or empty string for Vite proxy in development
    // Production will have full URL in .env.production
    this.baseUrl = import.meta.env.VITE_API_BASE_URL || '';
  }

  /**
   * Create a new share
   */
  async createShare(shareData: ShareData): Promise<ShareResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/api/shares`, {
        method: 'POST',
        headers: getAPIHeaders(),
        body: JSON.stringify(shareData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || `Failed to create share: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Share creation failed:', error);
      throw error;
    }
  }

  /**
   * Get share details by ID
   */
  async getShare(shareId: string): Promise<ShareDetails> {
    try {
      const response = await fetch(`${this.baseUrl}/api/shares/${shareId}`);

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Share not found');
        }
        const error = await response.json();
        throw new Error(error.message || `Failed to retrieve share: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Share retrieval failed:', error);
      throw error;
    }
  }

  /**
   * Get share metadata (lighter endpoint without HTML content)
   */
  async getShareMetadata(shareId: string): Promise<Omit<ShareDetails, 'htmlContent' | 'code'>> {
    try {
      const response = await fetch(`${this.baseUrl}/api/shares/${shareId}/metadata`);

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Share not found');
        }
        const error = await response.json();
        throw new Error(error.message || `Failed to retrieve share metadata: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Share metadata retrieval failed:', error);
      throw error;
    }
  }

  /**
   * Generate share URL from share ID
   */
  generateShareUrl(shareId: string): string {
    const frontendUrl = window.location.origin;
    return `${frontendUrl}/?share=${shareId}`;
  }

  /**
   * Copy share URL to clipboard
   */
  async copyToClipboard(shareUrl: string): Promise<void> {
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        try {
          await navigator.clipboard.writeText(shareUrl);
          console.log('✅ Copied to clipboard using Clipboard API');
          return;
        } catch (clipboardError) {
          console.warn('Clipboard API failed, trying fallback:', clipboardError);
        }
      }

      // Fallback method - works in more contexts
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        const successful = document.execCommand('copy');
        if (!successful) {
          throw new Error('execCommand copy returned false');
        }
        console.log('✅ Copied to clipboard using execCommand fallback');
      } finally {
        textArea.remove();
      }
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      throw new Error('Failed to copy URL to clipboard');
    }
  }

  /**
   * Extract original code from container or data attributes
   */
  extractCodeFromHTML(container: HTMLElement): string {
    // Try to get original code from data attribute first
    const originalCode = container.dataset.originalCode;
    if (originalCode) {
      return originalCode;
    }

    // Fallback: extract text content and clean it up
    const codeElements = container.querySelectorAll('code, pre, .code, .language-');
    if (codeElements.length > 0) {
      return Array.from(codeElements)
        .map(el => el.textContent || '')
        .join('\n')
        .trim();
    }

    // Last resort: use all text content
    return container.textContent?.trim() || '';
  }
}

// Export singleton instance
export const shareService = new ShareService();
export default shareService;